import { currencyFormatter } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Wallet } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import FormButtons from '../_components/form-buttons'

const FUNDING_SOURCE_ERROR_MESSAGE = 'Please select a funding source' as const

const purseSelectionSchema = z.object({
  purseId: z
    .string({
      message: FUNDING_SOURCE_ERROR_MESSAGE,
    })
    .min(1, FUNDING_SOURCE_ERROR_MESSAGE),
})

type PurseSelectionFormData = z.infer<typeof purseSelectionSchema>

interface SelectPurseStepProps {
  purses: Purse[]
  selectedPurseId: string
  onPurseSelect: (purseId: string) => void
  onNext: () => void
  onCancelClick: () => void
}

export default function SelectPurseStep({
  purses,
  selectedPurseId,
  onPurseSelect,
  onNext,
  onCancelClick,
}: SelectPurseStepProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PurseSelectionFormData>({
    resolver: zodResolver(purseSelectionSchema),
    defaultValues: {
      purseId: selectedPurseId ?? '',
    },
    mode: 'onChange',
  })

  const watchedPurseId = watch('purseId')

  const onSubmit = (data: PurseSelectionFormData) => {
    onPurseSelect(data.purseId)
    onNext()
  }

  const handlePurseSelect = (purseId: string) => {
    setValue('purseId', purseId, { shouldValidate: true })
    onPurseSelect(purseId)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="h-5 w-5 text-gray-900" />
          <h2 className="text-base font-semibold text-gray-900">
            Select Funding Source
          </h2>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Choose which purse will fund these scholarships. The available balance
          must be sufficient to cover all awards.
        </p>
        <div className="space-y-3  overflow-y-auto">
          {[...purses].map((purse) => {
            const isSelected = watchedPurseId === purse.id.toString()
            return (
              <div
                key={purse.id}
                className={`
                  relative flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all
                  ${
                    isSelected
                      ? 'border-primary bg-gray-50 '
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                onClick={() => handlePurseSelect(purse.id.toString())}
              >
                <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                  <input
                    {...register('purseId')}
                    type="radio"
                    id={`purse-${purse.id}`}
                    value={purse.id.toString()}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-base mb-1">
                        {purse.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>Available Balance:</span>
                        <span className="font-medium text-gray-900">
                          {currencyFormatter.format(purse.balance_available)}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="flex items-center justify-center w-6 h-6 bg-primary rounded-full ml-2">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {errors.purseId && (
          <div className="mb-4 p-3 mt-3 bg-danger/5 border border-danger/20 rounded-lg">
            <p className="text-sm text-danger font-medium">
              {errors.purseId.message}
            </p>
          </div>
        )}{' '}
      </div>
      <div>
        <FormButtons>
          <FormButtons.BackButton buttonText="Cancel" onClick={onCancelClick} />
          <FormButtons.NextButton type="submit" />
        </FormButtons>
      </div>
    </form>
  )
}
